// Hand-rolled trailing-edge debounce. Per spec/tech-arch.md §4 (file
// structure: `src/lib/debounce.ts`) + spec/build-plan.md P-021 row.
//
// **Why hand-rolled (no library).** Per OC §3.8 third-party-vs-hand-rolled
// check: lodash.debounce is ~50 LOC of dependency for what amounts to
// ~15 LOC of pure logic. The Build Plan already files this at
// `src/lib/debounce.ts` as a hand-rolled helper. Two consumers in this
// codebase — persistence.ts (P-021 write-through) and the future
// useDebounced hook (P-029 preview iframe srcdoc).
//
// **Trailing-edge semantics.** The wrapper schedules `fn` to fire `ms`
// after the LAST call within the window. Subsequent calls reset the
// timer. Best fit for "save-on-stop-typing" UX (Tech Arch §5.1's
// "debounced (500ms) write of the full state"). Leading-edge semantics
// (fire-then-suppress) and throttle semantics (fire-once-per-window-then-
// drop) are different patterns; use a different helper if they're
// needed.
//
// **`cancel()` for cleanup.** Returns the wrapper with a `.cancel()`
// method that aborts any pending invocation. Important for two paths:
// (1) `attachPersistence`'s detach flow calls cancel to prevent a write
// from firing post-detach (test isolation, P-022 unmount); (2) future
// preview-iframe unmount cancels in-flight srcdoc updates.
//
// **`T extends (...args: never[]) => void` constraint.** The `never[]`
// in contravariant position is the canonical TS pattern for "any
// function signature returning void." Inside the body, `Parameters<T>`
// extracts T's actual parameters; the cast on return preserves T's
// callable signature so the wrapper accepts the same args as the
// original function.

export function debounce<T extends (...args: never[]) => void>(
  fn: T,
  ms: number,
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const debounced = (...args: Parameters<T>): void => {
    if (timeoutId !== undefined) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      timeoutId = undefined
      fn(...args)
    }, ms)
  }

  debounced.cancel = (): void => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }
  }

  return debounced as T & { cancel: () => void }
}
